/**
 * Admin — Shipping Rules per Region (4-tier batch editor).
 * Route: /admin/shipping-tiers
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Section, Card, Badge, Button, Alert, Input, Label } from "../design-system";
import { useAuth } from "../auth/AuthProvider";

const ITALIAN_REGIONS = [
  "Abruzzo","Basilicata","Calabria","Campania","Emilia-Romagna",
  "Friuli-Venezia Giulia","Lazio","Liguria","Lombardia","Marche",
  "Molise","Piemonte","Puglia","Sardegna","Sicilia","Toscana",
  "Trentino-Alto Adige","Umbria","Valle d'Aosta","Veneto",
];

const DEFAULT_TEMPLATE = [
  { min_order_amount: 0,       max_order_amount: 300, transport_cost: 45 },
  { min_order_amount: 300.01,  max_order_amount: 600, transport_cost: 30 },
  { min_order_amount: 600.01,  max_order_amount: 900, transport_cost: 15 },
  { min_order_amount: 900.01,  max_order_amount: null, transport_cost: 0 },
];

function fmtEur(v) { return v == null ? "∞" : `€ ${Number(v).toFixed(2).replace(".", ",")}`; }

export function AdminShippingTiersPage() {
  const { apiGet, token } = useAuth();
  const [region, setRegion] = useState("Lombardia");
  const [validFrom, setValidFrom] = useState("2026-01-01");
  const [validTo, setValidTo] = useState("2026-12-31");
  const [tiers, setTiers] = useState(DEFAULT_TEMPLATE);
  const [err, setErr] = useState(null);
  const [flash, setFlash] = useState(null);
  const [busy, setBusy] = useState(false);
  const [configured, setConfigured] = useState(false);

  const backend = process.env.REACT_APP_BACKEND_URL || "";

  const load = async () => {
    setErr(null); setFlash(null);
    try {
      const d = await apiGet(`/api/admin/shipping-rules/by-region/${encodeURIComponent(region)}`);
      if (d.configured) {
        setTiers(d.tiers.map((t) => ({
          min_order_amount: Number(t.min_order_amount || 0),
          max_order_amount: t.max_order_amount == null ? null : Number(t.max_order_amount),
          transport_cost: Number(t.transport_cost || 0),
        })));
        setValidFrom(d.valid_from || "2026-01-01");
        setValidTo(d.valid_to || "");
        setConfigured(true);
      } else {
        setTiers(DEFAULT_TEMPLATE);
        setConfigured(false);
      }
    } catch (e) { setErr(e?.message || String(e)); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [region]);

  const upd = (i, k, v) => {
    const t = [...tiers];
    t[i] = { ...t[i], [k]: v === "" ? null : Number(v) };
    setTiers(t);
  };

  const save = async () => {
    setBusy(true); setErr(null); setFlash(null);
    try {
      const res = await fetch(`${backend}/api/admin/shipping-rules/by-region/${encodeURIComponent(region)}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token || ""}`,
        },
        body: JSON.stringify({
          region, valid_from: validFrom, valid_to: validTo || null,
          tiers: tiers.map((t) => ({
            min_order_amount: Number(t.min_order_amount || 0),
            max_order_amount: t.max_order_amount == null ? null : Number(t.max_order_amount),
            transport_cost: Number(t.transport_cost || 0),
          })),
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      const d = await res.json();
      setFlash(`Salvati ${d.count} scaglioni per ${region}.`);
      setConfigured(true);
      await load();
      setTimeout(() => setFlash(null), 3000);
    } catch (e) {
      const t = String(e?.message || e);
      let human = t;
      if (t.includes("exactly_4_tiers_required")) human = "Devono esserci esattamente 4 scaglioni.";
      else if (t.includes("overlap")) human = "Gli scaglioni si sovrappongono.";
      else if (t.includes("gap")) human = "Ci sono buchi tra gli scaglioni.";
      else if (t.includes("min_must_be_lt_max")) human = "Ogni scaglione deve avere min < max.";
      setErr(human);
    } finally { setBusy(false); }
  };

  return (
    <Section title="Scaglioni Trasporto — per Regione"
             subtitle="Configura i 4 scaglioni di contributo trasporto per la Regione selezionata"
             actions={<Link to="/admin"><Button size="sm">← Home Admin</Button></Link>}
             data-testid="admin-shipping-tiers-page">
      {err && <Alert tone="danger">Errore: {String(err)}</Alert>}
      {flash && <Alert tone="success">{flash}</Alert>}

      <Card>
        <Card.Body>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: 10 }}>
            <div>
              <Label>Regione</Label>
              <select data-testid="tiers-region"
                      value={region} onChange={(e) => setRegion(e.target.value)}
                      style={{ width: "100%", padding: 8, border: "1px solid #cbd5e1", borderRadius: 6 }}>
                {ITALIAN_REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <div className="muted" style={{ fontSize: ".78rem", marginTop: 4 }}>
                {configured ? <Badge tone="success">Configurata</Badge> : <Badge tone="neutral">Non ancora configurata</Badge>}
              </div>
            </div>
            <div>
              <Label>Valido dal</Label>
              <Input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)}
                     data-testid="tiers-valid-from" />
            </div>
            <div>
              <Label>Valido al (opzionale)</Label>
              <Input type="date" value={validTo || ""} onChange={(e) => setValidTo(e.target.value)}
                     data-testid="tiers-valid-to" />
            </div>
            <div style={{ alignSelf: "flex-end" }}>
              <Button variant="primary" onClick={save} loading={busy} disabled={busy}
                      data-testid="tiers-save">
                {busy ? "Salvataggio…" : "Salva 4 scaglioni"}
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>

      <Card style={{ marginTop: 16 }}>
        <Card.Header><strong>Scaglioni contributo trasporto</strong></Card.Header>
        <Card.Body>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f8fafc", textAlign: "left" }}>
                <th style={{ padding: 8 }}>Scaglione</th>
                <th style={{ padding: 8 }}>Importo MIN ordine (€)</th>
                <th style={{ padding: 8 }}>Importo MAX ordine (€)</th>
                <th style={{ padding: 8 }}>Contributo trasporto (€)</th>
                <th style={{ padding: 8 }}>Riepilogo</th>
              </tr>
            </thead>
            <tbody>
              {tiers.map((t, i) => (
                <tr key={i} data-testid={`tier-row-${i+1}`} style={{ borderTop: "1px solid #e5e7eb" }}>
                  <td style={{ padding: 8 }}><strong>Scaglione {i + 1}</strong></td>
                  <td style={{ padding: 8 }}>
                    <Input type="number" step="0.01" value={t.min_order_amount ?? ""}
                           data-testid={`tier-min-${i+1}`}
                           onChange={(e) => upd(i, "min_order_amount", e.target.value)} />
                  </td>
                  <td style={{ padding: 8 }}>
                    <Input type="number" step="0.01"
                           value={t.max_order_amount == null ? "" : t.max_order_amount}
                           data-testid={`tier-max-${i+1}`}
                           placeholder={i === 3 ? "∞ (porto franco)" : ""}
                           onChange={(e) => upd(i, "max_order_amount", e.target.value)} />
                  </td>
                  <td style={{ padding: 8 }}>
                    <Input type="number" step="0.01" value={t.transport_cost ?? 0}
                           data-testid={`tier-cost-${i+1}`}
                           onChange={(e) => upd(i, "transport_cost", e.target.value)} />
                  </td>
                  <td style={{ padding: 8, fontSize: ".85rem" }}>
                    {fmtEur(t.min_order_amount)}–{t.max_order_amount == null ? "∞" : fmtEur(t.max_order_amount)}
                    {" → "}
                    {t.transport_cost > 0
                      ? <strong>{fmtEur(t.transport_cost)}</strong>
                      : <Badge tone="success">Porto franco</Badge>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="muted" style={{ fontSize: ".78rem", marginTop: 12, marginBottom: 0 }}>
            Regole di validazione backend: esattamente 4 scaglioni · min &lt; max ·
            nessuna sovrapposizione · nessun buco · il 4° scaglione può avere max infinito
            (porto franco). Il Pricing Service resta unica fonte di verità per il calcolo.
          </p>
        </Card.Body>
      </Card>
    </Section>
  );
}
